import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faStar,
  faMobileScreenButton,
  faMoneyBillTransfer,
  faBuildingColumns,
  faGlobe,
  faLink,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import './PricingArea.css';

// Same package colors as the courses list
const TABS = [
  {
    key: 'standard',
    label: 'Standard',
    color: '#F7931E',
    ink: '#0A1230',
    note: 'For all Standard courses (orange)',
    match: (name) => ['Starter', 'Advanced', 'Premium'].includes(name),
  },
  {
    key: 'mastery',
    label: 'Mastery',
    color: '#C9CFD8',
    ink: '#0A1230',
    note: 'For AI Creators, Web Mastery: PHP & MySQL and AI & Python',
    match: (name) => name.includes('Mastery'),
  },
  {
    key: 'robotics',
    label: 'Robotics',
    color: '#3B7BFF',
    ink: '#FFFFFF',
    note: 'For Smart Robot micro:bit, kit delivered home',
    match: (name) => name.includes('Robotics'),
  },
];

const ORDER = ['Starter', 'Advanced', 'Premium', 'Mastery Starter', 'Mastery Premium', 'Robotics Advanced'];

const THEME_NAME = {
  Starter: 'Starter Rocket',
  Advanced: 'Space Coder',
  Premium: 'Galaxy Builder',
  'Robotics Advanced': 'Robotics Explorer',
  'Mastery Starter': 'Code Pioneer',
  'Mastery Premium': 'Quantum Architect',
};

// Same for every package, so it's shown once instead of on each card
const INCLUDED = [
  'Live 1-to-1 classes',
  'Flexible scheduling',
  'Expert instructors',
  'Project-based learning',
  'Progress reports',
  'Certificate of completion',
  'Lifetime access to materials',
];

// Ways families can pay (details are sent on WhatsApp)
const PAYMENT_METHODS = [
  { icon: faMobileScreenButton, label: 'Whish Money' },
  { icon: faMoneyBillTransfer, label: 'OMT' },
  { icon: faMoneyBillTransfer, label: 'BOB Finance' },
  { icon: faGlobe, label: 'Western Union' },
  { icon: faLink, label: 'Payment link' },
];

const money = (v) => {
  const n = Number(v);
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
};

const whatsappUrl = (text) =>
  `https://api.whatsapp.com/send/?phone=${process.env.REACT_APP_MOBILE_NUMBER1}&text=${encodeURIComponent(
    text
  )}&type=phone_number&app_absent=0`;

// Use <PricingArea asPage /> on the Pricing Plans page so its title becomes the page <h1>
const PricingArea = ({ asPage = false }) => {
  const Heading = asPage ? 'h1' : 'h2';
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [activeTab, setActiveTab] = useState('standard');

  useEffect(() => {
    let cancelled = false;
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/getprices`);
        if (cancelled) return;
        if (response.data.success && Array.isArray(response.data.data)) {
          setPlans(response.data.data);
          setStatus('ready');
        } else {
          setStatus('error');
        }
      } catch (err) {
        if (!cancelled) setStatus('error');
      }
    };
    fetchPlans();
    return () => {
      cancelled = true;
    };
  }, []);

  const tab = TABS.find((t) => t.key === activeTab);
  const visible = plans
    .filter((p) => tab.match(p.name))
    .sort((a, b) => ORDER.indexOf(a.name) - ORDER.indexOf(b.name));

  return (
    <section className="rmp" id="pricing" aria-labelledby="rmp-title">
      <div className="rmp-inner">
        <header className="rmp-header">
          <span className="rmp-eyebrow">Packages</span>
          <Heading id="rmp-title" className="rmp-title">
            Simple packages, <span>first class free</span>
          </Heading>
        </header>

        <div className="rmp-tabs" role="tablist" aria-label="Package type">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={activeTab === t.key}
              className={`rmp-tab ${activeTab === t.key ? 'is-active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              <span className="rmp-tab-dot" style={{ background: t.color }} aria-hidden="true" />
              {t.label}
            </button>
          ))}
        </div>
        <p className="rmp-note">{tab.note}</p>

        {status === 'loading' && (
          <div className="rmp-grid" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rmp-card rmp-skeleton" />
            ))}
          </div>
        )}

        {status === 'error' && (
          <div className="rmp-fallback">
            <p>Prices couldn’t load right now. Message us and we’ll send them in a minute.</p>
            <a href={whatsappUrl("Hi! Can you send me RoboMind's package prices?")} target="_blank" rel="noopener noreferrer" className="rmp-btn">
              <FontAwesomeIcon icon={faWhatsapp} />
              Ask on WhatsApp
            </a>
          </div>
        )}

        {status === 'ready' && (
          <div key={activeTab} className={`rmp-grid rmp-grid--${visible.length}`}>
            {visible.map((plan) => {
              const hasOffer = plan.offerPrice > 0 && plan.offerPrice < plan.price;
              const current = hasOffer ? plan.offerPrice : plan.price;
              const discount = hasOffer ? Math.round(((plan.price - plan.offerPrice) / plan.price) * 100) : 0;
              const perSession =
                plan.sessionPrice != null ? Number(plan.sessionPrice) : plan.sessionCount ? current / plan.sessionCount : null;
              const perWeek = plan.sessionperWeek || 2;
              const months = Math.max(1, Math.round(plan.sessionCount / (perWeek * 4)));
              const isPopular = plan.name === 'Premium';
              const name = THEME_NAME[plan.name] || plan.name;

              return (
                <article
                  key={plan.id ?? plan.name}
                  className={`rmp-card rmp-card--${activeTab} ${isPopular ? 'is-popular' : ''}`}
                  style={{ '--rmp-accent': tab.color, '--rmp-ink': tab.ink }}
                >
                  <div className="rmp-card-top">
                    <span className="rmp-tier">{plan.name}</span>
                    {isPopular ? (
                      <span className="rmp-popular">
                        <FontAwesomeIcon icon={faStar} /> Most popular
                      </span>
                    ) : discount > 0 ? (
                      <span className="rmp-off">{discount}% off</span>
                    ) : null}
                  </div>

                  <h3 className="rmp-name">{name}</h3>

                  <div className="rmp-price">
                    <strong>{money(current)}</strong>
                    {hasOffer && <s>{money(plan.price)}</s>}
                  </div>
                  {perSession != null && (
                    <p className="rmp-per">
                      {money(perSession.toFixed(2))} per session
                      {isPopular && discount > 0 && <span className="rmp-off rmp-off--inline">{discount}% off</span>}
                    </p>
                  )}

                  <div className="rmp-meta">
                    <div className="rmp-sessions">
                      <strong>{plan.sessionCount}</strong>
                      <span>sessions</span>
                    </div>
                    <div className="rmp-meta-sub">
                      <span>{perWeek} sessions / week</span>
                      <span>
                        about {months} month{months > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  <a
                    href={whatsappUrl(`Hi! I'm interested in the ${name} package (${plan.sessionCount} sessions).`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`rmp-btn ${isPopular ? '' : 'rmp-btn--ghost'}`}
                  >
                    <FontAwesomeIcon icon={faWhatsapp} />
                    Enroll now
                  </a>
                </article>
              );
            })}
          </div>
        )}

        <div className="rmp-included">
          <span className="rmp-included-title">Included in every package</span>
          <ul>
            {INCLUDED.map((item) => (
              <li key={item}>
                <FontAwesomeIcon icon={faCheck} />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="rmp-pay">
          <span className="rmp-pay-title">Ways to pay</span>
          <ul>
            {PAYMENT_METHODS.map((m) => (
              <li key={m.label}>
                <FontAwesomeIcon icon={m.icon} />
                {m.label}
              </li>
            ))}
          </ul>
          <p className="rmp-pay-note">From Lebanon or abroad. We send the payment details on WhatsApp.</p>
        </div>

        <p className="rmp-footnote">Not sure which package? Start with a free trial class and we’ll recommend one.</p>
      </div>
    </section>
  );
};

export default PricingArea;